import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  PopoverTrigger,
  Input,
  HStack,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { FiAlertCircle } from "react-icons/fi";
import GaraazGeneralPopover from "./GaraazGeneralPopover";

export type PartSearchMismatchDetails = {
  search: string;
  selectedPartNumber: string;
  selectedPartName: string;
};

type AddPartQuantityModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedPart: any | null;
  quantity: string;
  onQuantityChange: (quantity: string) => void;
  onConfirm: () => void;
  remove_idFromPartDetails: (partObject: any) => {
    "Part Name": any;
    "Part Number": any;
    MRP: any;
    Brand: any;
    "HSN code": any;
    "Root Part Number": any;
  };
  isLoading?: boolean;
  isDisabled?: boolean;
  /** When set, user cannot add — only dismiss with "OK I understand". */
  partSearchMismatch?: PartSearchMismatchDetails | null;
};

const AddPartQuantityModal: React.FC<AddPartQuantityModalProps> = ({
  isOpen,
  onClose,
  selectedPart,
  quantity,
  onQuantityChange,
  onConfirm,
  remove_idFromPartDetails,
  isLoading,
  isDisabled = false,
  partSearchMismatch,
}) => {
  const hasMismatch = Boolean(partSearchMismatch);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="lg" overflow="hidden">
        <ModalHeader pb={hasMismatch ? 2 : undefined}>
          {hasMismatch ? (
            <HStack align="flex-start" spacing={3}>
              <Icon
                as={FiAlertCircle}
                color="orange.500"
                boxSize={6}
                mt={0.5}
                flexShrink={0}
                aria-hidden
              />
              <Box>
                <Text fontSize="lg" fontWeight="semibold" lineHeight="short">
                  Part does not match search
                </Text>
                <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                  This part cannot be added. Compare the values below, then go
                  back and adjust your search or pick another row.
                </Text>
              </Box>
            </HStack>
          ) : (
            "Add Part Quantity"
          )}
        </ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        {hasMismatch ? <Divider /> : null}
        <ModalBody pt={hasMismatch ? 4 : undefined} pb={hasMismatch ? 2 : undefined}>
          {selectedPart && hasMismatch && partSearchMismatch ? (
            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              overflow="hidden"
              boxShadow="sm"
            >
              <Box px={4} py={3} bg="orange.50" borderBottomWidth="1px" borderColor="gray.200">
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  mb={1.5}
                >
                  Search (part number or name)
                </Text>
                <Text
                  fontSize="md"
                  fontWeight="medium"
                  color="gray.900"
                  wordBreak="break-word"
                  fontFamily="mono"
                  lineHeight="tall"
                >
                  {partSearchMismatch.search}
                </Text>
              </Box>
              <Box px={4} py={3} bg="white">
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  mb={1.5}
                >
                  Selected part
                </Text>
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  color="gray.900"
                  wordBreak="break-word"
                  fontFamily="mono"
                  lineHeight="tall"
                >
                  {partSearchMismatch.selectedPartNumber}
                </Text>
                {partSearchMismatch.selectedPartName ? (
                  <Text fontSize="sm" color="gray.600" mt={1.5} lineHeight="short">
                    {partSearchMismatch.selectedPartName}
                  </Text>
                ) : null}
              </Box>
            </Box>
          ) : null}
          {selectedPart && !hasMismatch ? (
            <Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Part</Th>
                      <Th textAlign={"center"}>Quantity</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>
                        <GaraazGeneralPopover
                          ArekeysVisible={true}
                          CopierKeys={[]}
                          title="Part Details"
                          value={[remove_idFromPartDetails(selectedPart)]}
                        >
                          <PopoverTrigger>
                            <Text
                              cursor={"pointer"}
                              noOfLines={1}
                              whiteSpace={"normal"}
                              fontSize={"sm"}
                              color={"#1a202c"}
                            >
                              {selectedPart.partNumber}
                            </Text>
                          </PopoverTrigger>
                        </GaraazGeneralPopover>
                      </Td>
                      <Td width={"40%"} textAlign={"center"}>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          size="sm"
                          value={Number(quantity || "")}
                          onChange={(e) => {
                            const numValue = Math.max(
                              0,
                              parseInt(e.target.value) || 0,
                            );
                            onQuantityChange(numValue.toString());
                          }}
                          min={0}
                          width="100px"
                          isDisabled={isDisabled || isLoading}
                        />
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </ModalBody>
        <ModalFooter pt={hasMismatch ? 2 : undefined}>
          {hasMismatch ? (
            <Button
              colorScheme="blue"
              size="md"
              onClick={onClose}
              width="full"
              borderRadius="md"
            >
              OK, I understand
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                mr={3}
                onClick={onClose}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={onConfirm}
                isDisabled={
                  isDisabled ||
                  !quantity ||
                  parseInt(quantity) < 1 ||
                  isLoading
                }
                isLoading={isLoading}
              >
                Add Part
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPartQuantityModal;
